import React, { createContext, useContext, useState, useCallback } from "react";
import { useApolloClient } from "@apollo/client";

const OperationContext = createContext();
export const useOperation = () => useContext(OperationContext);

const initialToast = {
  isVisible: false,
  message: "",
  type: "success",
  iconName: "iconCheck",
};

export const OperationProvider = ({ children }) => {
  const client = useApolloClient();
  const [toast, setToast] = useState(initialToast);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);

  const closeToast = useCallback(() => {
    setToast(initialToast);
  }, []);

  const showToast = useCallback((message, type = "success") => {
    const iconName =
      type === "success"
        ? "iconCheck"
        : type === "warning" || type === "info"
          ? "iconInfo"
          : "iconClose";

    setToast({ isVisible: true, type, message, iconName });
    setTimeout(() => setToast(initialToast), 4000);
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshFlag((f) => !f);
  }, []);

  const runOperation = useCallback(
    async (operationFn, { successMsg, errorMsg, refetchQueries = [] } = {}) => {
      setIsLoading(true);
      try {
        await operationFn();
        showToast(successMsg || "Operation succeeded!", "success");


        if (refetchQueries.length > 0) {
          await Promise.all(
            refetchQueries.map((q) => client.refetchQueries({ include: [q] }))
          );
        }

        triggerRefresh();

      } catch (error) {
        console.error(error);
        showToast(errorMsg || error.message || "Operation failed!", "error");
      } finally {
        setIsLoading(false);
      }
    },
    [client, showToast, triggerRefresh]
  );

  const withOperation = (operation, options = {}) => async (...args) => {
    try {
      setIsLoading(true);
      const result = await operation(...args);
  
      // If operation returns false → skip showing success toast
      if (result === false) return;
  
      showToast(options.successMsg || "Operation successful", "success");
      if (options.onSuccess) options.onSuccess();
    } catch (error) {
      showToast(options.errorMsg || "Operation failed", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OperationContext.Provider
      value={{
        runOperation,
        withOperation,
        showToast,
        closeToast,
        triggerRefresh,
        setIsLoading,
        toast,
        isLoading,
        refreshFlag,
      }}
    >
      {children}
    </OperationContext.Provider>
  );
};
